import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { X, RefreshCw, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { workerApiService } from '../../services/workerApi';
import { apiService } from '../../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  accountId: string;
  accountName?: string;
  onSuccess: () => void;
  onClose: () => void;
}

type Phase = 'starting' | 'qr' | 'success' | 'error';

// ── Component ─────────────────────────────────────────────────────────────────

export const ZaloQRModal: React.FC<Props> = ({ accountId, accountName, onSuccess, onClose }) => {
  const { t } = useTranslation('chatbot');
  const [phase, setPhase]       = useState<Phase>('starting');
  const [qrBase64, setQrBase64] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [retrying, setRetrying] = useState(false);

  const pollRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);

  // Stop polling helper
  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Start / restart QR session
  // Worker auto-starts QR login when MQTT browser_account_sync arrives.
  // We resolve the worker direct URL first so Zalo QR calls bypass CF Pages proxy.
  const startSession = async () => {
    setPhase('starting');
    setQrBase64(null);
    setErrorMsg('');
    cancelledRef.current = false;
    stopPolling();

    // Resolve worker URL → set direct mode so workerApiService routes to worker pod
    try {
      const info = await apiService.getWorkerURL();
      if (info?.worker?.url && info?.jwt_token) {
        workerApiService.setDirectMode(info.worker.url, info.jwt_token);
      }
    } catch {
      // If URL resolution fails, still attempt polling (may work via proxy in dev)
    }

    // Give worker a moment to spawn the browser before first poll
    await new Promise((r) => setTimeout(r, 1500));
    if (cancelledRef.current) return;
    setPhase('qr');
    startPolling();
  };

  // Poll QR and login status every 4s
  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      if (cancelledRef.current) {
        stopPolling();
        return;
      }
      try {
        // Fetch QR image
        const qrRes = await workerApiService.zaloGetQRCode(accountId);
        if (qrRes.status === 'not_started') return; // session not ready yet, keep waiting
        if (qrRes.qr_base64) {
          setQrBase64(qrRes.qr_base64);
        }

        // Check login success / failure
        const statusRes = await workerApiService.zaloGetLoginStatus(accountId);
        if (statusRes.status === 'success' && statusRes.logged_in) {
          stopPolling();
          setPhase('success');
          setTimeout(() => onSuccess(), 1500);
          return;
        }
        if (statusRes.status === 'failed') {
          stopPolling();
          setPhase('error');
          setErrorMsg(t('zaloLoginFailed'));
        }
      } catch {
        // Network error — keep polling silently
      }
    }, 4000);
  };

  // On mount: start session
  useEffect(() => {
    startSession();
    return () => {
      cancelledRef.current = true;
      stopPolling();
      workerApiService.zaloCancelLogin(accountId).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // Handle retry
  const handleRetry = async () => {
    setRetrying(true);
    try {
      await startSession();
    } finally {
      setRetrying(false);
    }
  };

  // Handle close
  const handleClose = () => {
    cancelledRef.current = true;
    stopPolling();
    workerApiService.zaloCancelLogin(accountId).catch(() => {});
    onClose();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        className="bg-white rounded-2xl w-full max-w-sm flex flex-col shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {/* Zalo logo placeholder */}
            <div className="w-9 h-9 rounded-xl bg-[#0068ff] flex items-center justify-center shrink-0">
              <span className="text-white font-extrabold text-[13px] leading-none">Z</span>
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-gray-900 leading-tight">{t('zaloModalTitle')}</h2>
              {accountName && (
                <p className="text-[12px] text-gray-400 leading-tight">{accountName}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            title={t('close')}
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col items-center gap-4 px-6 py-8 min-h-[320px] justify-center">

          {/* Starting */}
          {phase === 'starting' && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-[#0068ff] flex items-center justify-center">
                <Loader2 size={28} className="text-white animate-spin" />
              </div>
              <p className="font-semibold text-gray-800 text-[15px]">{t('zaloStarting')}</p>
              <p className="text-[13px] text-gray-400 text-center">
                {t('zaloStartingDetail')}
              </p>
            </>
          )}

          {/* QR Code */}
          {phase === 'qr' && (
            <>
              {qrBase64 ? (
                <div className="relative">
                  <img
                    src={qrBase64}
                    alt="Zalo QR Code"
                    className="w-52 h-52 rounded-xl border border-gray-200 object-contain"
                  />
                </div>
              ) : (
                <div className="w-52 h-52 rounded-xl border border-gray-200 flex items-center justify-center bg-gray-50">
                  <Loader2 size={28} className="text-[#0068ff] animate-spin" />
                </div>
              )}
              <p className="text-[13px] text-gray-500 text-center leading-relaxed">
                {t('zaloScanInstructions')}
              </p>
              <button
                type="button"
                onClick={handleRetry}
                disabled={retrying}
                className="flex items-center gap-2 text-[13px] text-[#0068ff] hover:text-blue-700 font-medium transition-colors disabled:opacity-50"
              >
                {retrying ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <RefreshCw size={14} />
                )}
                {t('zaloRefreshQr')}
              </button>
            </>
          )}

          {/* Success */}
          {phase === 'success' && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center">
                <CheckCircle size={32} className="text-green-500" />
              </div>
              <p className="font-semibold text-gray-800 text-[15px]">{t('zaloLoginSuccess')}</p>
              <p className="text-[13px] text-gray-400 text-center">{t('zaloLoginSuccessDetail')}</p>
            </>
          )}

          {/* Error */}
          {phase === 'error' && (
            <>
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
                <AlertCircle size={32} className="text-red-400" />
              </div>
              <p className="font-semibold text-gray-800 text-[15px]">{t('zaloLoginFailed')}</p>
              <p className="text-[13px] text-red-400 text-center">{errorMsg}</p>
              <button
                type="button"
                onClick={handleRetry}
                disabled={retrying}
                className="mt-2 px-5 py-2 rounded-xl bg-[#0068ff] text-white text-[13px] font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {retrying && <Loader2 size={14} className="animate-spin" />}
                {t('zaloRefreshQr')}
              </button>
            </>
          )}
        </div>

        {/* Footer note */}
        {phase === 'qr' && (
          <div className="border-t border-gray-100 px-6 py-3 bg-blue-50/50">
            <p className="text-[12px] text-gray-400 text-center">
              {t('zaloQrNote')}
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ZaloQRModal;
